import React, { useMemo, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  Stack,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItemButton,
  ListItemText,
} from "@mui/material";
import LaunchIcon from "@mui/icons-material/Launch";
import DeleteIcon from "@mui/icons-material/Delete";

const MIN_QUERY = 3;

export default function TypeHostSelector({ value, onChange }) {
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [error, setError] = useState("");
  const [manualOpen, setManualOpen] = useState(false);
  const [resultsOpen, setResultsOpen] = useState(false);
  const [manual, setManual] = useState({
    scientific_name: "",
    scientific_name_authorship: "",
    taxon_rank: "",
  });

  const selected = value;

  const handleSearch = async () => {
    if (!query || query.trim().length < MIN_QUERY) return;
    setLoading(true);
    setError("");
    try {
      const url = `https://api.gbif.org/v1/species/suggest?q=${encodeURIComponent(
        query.trim()
      )}&limit=10`;
      const res = await fetch(url);
      if (!res.ok) {
        const msg = await res.text();
        throw new Error(msg || `GBIF search failed (${res.status})`);
      }
      const data = await res.json();
      setResults(Array.isArray(data) ? data : []);
      if (!Array.isArray(data) || data.length === 0) {
        setManualOpen(true); // ヒットなしで手動入力を促す
        setResultsOpen(false);
      } else {
        setResultsOpen(true);
      }
    } catch (e) {
      setError(e.message || "Search failed");
      setResults([]);
      setResultsOpen(false);
    } finally {
      setLoading(false);
    }
  };

  const handleUse = (hit) => {
    if (!hit) return;
    onChange?.({
      source: "gbif",
      gbifId: hit.key,
      scientific_name: hit.scientificName || hit.canonicalName || "",
      scientific_name_authorship: hit.authorship || "",
      taxon_rank: hit.rank || "",
      taxonomic_status: hit.status || "",
      nomenclatural_code: hit.nomenclaturalCode || "",
      nomenclatural_status: hit.nomenclaturalStatus || "",
      kingdom: hit.kingdom || "",
      phylum: hit.phylum || "",
      class: hit.class || "",
      order: hit.order || "",
      superfamily: hit.superfamily || "",
      family: hit.family || "",
      subfamily: hit.subfamily || "",
      tribe: hit.tribe || "",
      subtribe: hit.subtribe || "",
      genus: hit.genus || "",
      subgenus: hit.subgenus || "",
      specific_epithet: hit.specificEpithet || hit.species || "",
      infraspecific_epithet: hit.infraspecificEpithet || "",
      higher_classification: hit.higherClassification || "",
      taxon_remarks: hit.taxonRemarks || "",
      source_url: hit.key ? `https://www.gbif.org/species/${hit.key}` : "",
    });
    setManualOpen(false);
    setResultsOpen(false);
  };

  const handleManualSubmit = () => {
    if (!manual.scientific_name.trim() || !manual.taxon_rank.trim()) return;
    onChange?.({
      source: "manual",
      gbifId: null,
      scientific_name: manual.scientific_name.trim(),
      scientific_name_authorship: manual.scientific_name_authorship.trim(),
      taxon_rank: manual.taxon_rank.trim(),
      taxonomic_status: "manual",
    });
    setResultsOpen(false);
  };

  const displayLabel = useMemo(() => {
    if (!selected) return "";
    const name = selected.scientific_name || "";
    const auth = selected.scientific_name_authorship || "";
    const rank = selected.taxon_rank || "";
    return `${name}${auth ? ` ${auth}` : ""}${rank ? ` [${rank}]` : ""}`;
  }, [selected]);

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="h6" sx={{ color: "teal", mb: 1 }}>
        Type Host (original description only)
      </Typography>
      <Typography variant="body2" sx={{ mb: 1, color: "text.secondary" }}>
        Original description に記載の Type host のみを登録してください。後続研究で判明したホストは
        Bionomics から登録します。
      </Typography>

      {!selected && (
        <Box>
          <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
            <TextField
              label="Host name"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              fullWidth
              size="small"
              helperText="3文字以上入力して検索"
            />
            <Button
              variant="contained"
              onClick={handleSearch}
              disabled={loading || !query || query.trim().length < MIN_QUERY}
            >
              {loading ? "Searching..." : "GBIFで検索"}
            </Button>
          </Stack>
          {error ? (
            <Typography variant="caption" color="error" sx={{ display: "block", mb: 1 }}>
              {error}
            </Typography>
          ) : null}

          <Button size="small" onClick={() => setManualOpen((p) => !p)}>
            {manualOpen ? "手動入力を閉じる" : "ヒットしない場合はこちら（手動入力）"}
          </Button>
          {manualOpen && (
            <Stack spacing={1} sx={{ mt: 1, mb: 1 }}>
              <TextField
                label="Scientific Name *"
                value={manual.scientific_name}
                onChange={(e) => setManual((m) => ({ ...m, scientific_name: e.target.value }))}
                fullWidth
              />
              <TextField
                label="Authorship"
                value={manual.scientific_name_authorship}
                onChange={(e) =>
                  setManual((m) => ({ ...m, scientific_name_authorship: e.target.value }))
                }
                fullWidth
              />
              <TextField
                label="Rank *"
                value={manual.taxon_rank}
                onChange={(e) => setManual((m) => ({ ...m, taxon_rank: e.target.value }))}
                fullWidth
              />
              <Button variant="contained" size="small" onClick={handleManualSubmit}>
                Use this host
              </Button>
            </Stack>
          )}
        </Box>
      )}

      {selected && (
        <Card variant="outlined" sx={{ mt: 1 }}>
          <CardContent>
            <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  {displayLabel}
                </Typography>
                {selected.source === "gbif" && selected.gbifId ? (
                  <Link
                    href={`https://www.gbif.org/species/${selected.gbifId}`}
                    target="_blank"
                    rel="noreferrer"
                    sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
                  >
                    GBIF {selected.gbifId} <LaunchIcon fontSize="inherit" />
                  </Link>
                ) : null}
                {selected.taxon_rank ? (
                  <Typography variant="body2" color="text.secondary">
                    Rank: {selected.taxon_rank}
                  </Typography>
                ) : null}
              </Box>
              <Stack direction="row" spacing={1}>
                <Button
                  size="small"
                  color="error"
                  startIcon={<DeleteIcon fontSize="small" />}
                  onClick={() => onChange?.(null)}
                >
                  Remove
                </Button>
                <Button size="small" onClick={() => setResultsOpen(true)}>
                  Re-search
                </Button>
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      )}

      {results.length > 0 && (
        <Dialog open={resultsOpen} onClose={() => setResultsOpen(false)} fullWidth maxWidth="sm">
          <DialogTitle>GBIF検索結果</DialogTitle>
          <DialogContent dividers sx={{ p: 0 }}>
            <List dense>
              {results.map((hit) => (
                <ListItemButton
                  key={hit.key}
                  alignItems="flex-start"
                  onClick={() => {
                    handleUse(hit);
                    setResultsOpen(false);
                  }}
                >
                  <ListItemText
                    primary={hit.scientificName || hit.canonicalName || ""}
                    secondary={
                      <span>
                        {hit.authorship || ""} <br />
                        {hit.rank || ""} {hit.family ? ` / ${hit.family}` : ""}
                      </span>
                    }
                  />
                </ListItemButton>
              ))}
            </List>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setResultsOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
}
